import type { PolicyViolation } from './policy';

export const QUERY_STATUSES = [
  'QUEUED',
  'RUNNING',
  'SUCCESS',
  'ERROR',
  'CANCELLED',
  'TIMEOUT',
  'DENIED',
] as const;
export type QueryStatus = (typeof QUERY_STATUSES)[number];

/**
 * Row-count choices offered in the console, following phpMyAdmin's ladder.
 *
 * The server's configured ceiling still applies — picking 10,000 on a
 * connection capped at 1,000 yields 1,000. Options above the ceiling are
 * shown disabled rather than hidden, so the cap is visible rather than
 * mysterious.
 */
export const ROW_LIMIT_OPTIONS = [25, 50, 100, 250, 500, 1000, 5000, 10000] as const;
export type RowLimitOption = (typeof ROW_LIMIT_OPTIONS)[number];

export const DEFAULT_ROW_LIMIT: RowLimitOption = 500;

export interface ExecuteQueryRequest {
  dataSourceId: string;
  sql: string;
  /**
   * Client-generated id, echoed back. Lets the console correlate a response
   * with the editor tab that sent it, and makes submits idempotent if the
   * network retries.
   */
  clientQueryId: string;
  /** Analyst-requested cap. Clamped down to the server maximum, never up. */
  maxRows?: number;
  /**
   * The schema unqualified table names resolve against — what the sidebar
   * calls the active database.
   *
   * Sent explicitly rather than left to the connection's own default so that
   * `FROM user` means the same thing in the editor, in the guard and in the
   * engine. Omitted only by callers that have no opinion, which fall back to
   * the connection's configured database.
   */
  database?: string;
}

/** A column in a result set, as described to the client. */
export interface ResultColumn {
  name: string;
  /** Engine-native type, for right-aligning numbers and formatting dates. */
  dataType: string;
  /** Present when policy rewrote this column's values. */
  masked: boolean;
}

/**
 * Rows are arrays, not objects, deliberately: a 10k-row result of 30 columns
 * is roughly 3x smaller on the wire without repeating every key per row, and
 * it preserves duplicate column names from joins that an object would collapse.
 */
export type ResultRow = Array<string | number | boolean | null>;

export interface QueryResult {
  queryRunId: string;
  clientQueryId: string;
  status: QueryStatus;
  columns: ResultColumn[];
  rows: ResultRow[];
  rowCount: number;
  /**
   * True when the row cap stopped the read before the result was exhausted.
   * The console shows a banner — silently truncating an analyst's result is
   * how people end up reporting wrong numbers.
   */
  truncated: boolean;
  effectiveLimit: number;
  durationMs: number;
  /** Time spent in the database, excluding queueing and serialisation. */
  executionMs: number;
  startedAt: string;
  finishedAt: string | null;
  /** Set when status is ERROR or TIMEOUT. */
  error: QueryError | null;
  /** Set when status is DENIED. */
  violations: PolicyViolation[];
  /** Notices worth showing but not worth failing over. */
  warnings: string[];
}

export interface QueryError {
  /** Engine error code, e.g. MySQL "ER_BAD_FIELD_ERROR". */
  code: string | null;
  message: string;
  /**
   * Where in the submitted SQL the engine objected, when it tells us. MySQL
   * reports a character offset in some errors; we translate to line/column so
   * Monaco can place a squiggle.
   */
  position?: { line: number; column: number };
  /** Suggested fix, when the message is recognisable (e.g. unknown column). */
  hint?: string | null;
}

/** Server-side syntax/policy check run as the analyst types, before any execution. */
export interface ValidateQueryRequest {
  dataSourceId: string;
  sql: string;
  /** Same meaning as on {@link ExecuteQueryRequest}. */
  database?: string;
}

export interface ValidateQueryResponse {
  valid: boolean;
  violations: PolicyViolation[];
  referencedTables: string[];
  maskedColumns: string[];
  /** Non-blocking advice: missing WHERE on a large table, SELECT *, etc. */
  warnings: Array<{
    message: string;
    position?: { line: number; column: number; endLine?: number; endColumn?: number };
  }>;
}

/** One entry in the analyst's own history, or in an auditor's cross-user view. */
export interface QueryHistoryEntry {
  id: string;
  dataSourceId: string;
  dataSourceName: string;
  sql: string;
  status: QueryStatus;
  rowCount: number | null;
  durationMs: number | null;
  startedAt: string;
  /** Present on an auditor's view; omitted from an analyst's own history. */
  user?: { id: string; name: string; email: string; department: string | null };
  deniedReason: string | null;
  errorMessage: string | null;
  exported: boolean;
}

export interface SavedQueryInput {
  name: string;
  description?: string | null;
  sql: string;
  dataSourceId: string;
  /** Shared queries are visible to everyone but editable only by the owner. */
  shared?: boolean;
}

export interface SavedQuery {
  id: string;
  name: string;
  description: string | null;
  sql: string;
  dataSourceId: string;
  ownerId: string;
  ownerName: string;
  /** Shared queries are visible to everyone but editable only by the owner. */
  shared: boolean;
  /**
   * Ids of the people this query was handed to individually.
   *
   * Separate from `shared`, which publishes it to everyone. Empty for a
   * private query, and only populated for people allowed to see who it went
   * to — the owner and administrators.
   */
  sharedWith: string[];
  /** True when the caller can see this only because it was shared with them. */
  sharedWithMe: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Replaces the set of people a saved query is shared with. */
export interface SetSavedQuerySharesInput {
  userIds: string[];
}

export const EXPORT_FORMATS = ['CSV', 'JSON', 'XLSX'] as const;
export type ExportFormat = (typeof EXPORT_FORMATS)[number];

export interface ExportRequest {
  queryRunId: string;
  format: ExportFormat;
  /**
   * Required free-text justification. Recorded in the audit log against the
   * export. Taking data out of the tool is the moment that actually matters,
   * so it is the one action that asks the analyst to state why.
   */
  reason: string;
}
